import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Descriptions,
  Button,
  Space,
  Tag,
  Alert,
  Collapse,
  List,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  message,
  Spin,
  theme
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckOutlined,
  UndoOutlined
} from '@ant-design/icons';
import validationService from '../../../../services/validationService';
import { formatCurrency } from '../../utils';
import {
  ValidationModalProps,
  ValidationResult,
  ValidationError,
  ValidationWarning
} from '../../../../types/validation';

const { Panel } = Collapse;
const { Text, Title } = Typography;

const ModalValidationIndividuelle: React.FC<ValidationModalProps> = ({
  visible,
  calculation: suppliedCalculation,
  onCancel,
  onValidate,
  onUnvalidate,
  loading = false
}) => {
  const { token } = theme.useToken();
  const [calculationDetails, setCalculationDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const requestVersion = useRef(0);
  const activeSelection = useRef({ visible, id: suppliedCalculation?.id });
  activeSelection.current = { visible, id: suppliedCalculation?.id };
  const hasCurrentDetails = calculationDetails && calculationDetails.id === suppliedCalculation?.id;
  const calculation = hasCurrentDetails ? { ...suppliedCalculation, ...calculationDetails } : suppliedCalculation;
  const validationResult: ValidationResult | null = hasCurrentDetails ? calculationDetails.validation_result : null;

  useEffect(() => {
    setCalculationDetails(null);
    setDetailsError(false);
    if (visible && suppliedCalculation) {
      loadValidationDetails();
    }
    return () => { requestVersion.current++; };
  }, [visible, suppliedCalculation]);

  const loadValidationDetails = async () => {
    const calculationId = suppliedCalculation?.id;
    if (!calculationId || !activeSelection.current.visible || activeSelection.current.id !== calculationId) return;
    const version = ++requestVersion.current;
    const isCurrentRequest = () => version === requestVersion.current
      && activeSelection.current.visible && activeSelection.current.id === calculationId;
    
    try {
      setIsLoading(true);
      setCalculationDetails(null);
      setDetailsError(false);
      // Reload the server's current checks after every validation action.
      const response = await validationService.getCalculationDetails(calculationId);
      if (!isCurrentRequest()) return;
      if (!response.success || !response.data?.validation_result) {
        throw new Error('Détails de validation indisponibles');
      }
      setCalculationDetails({ ...suppliedCalculation, ...response.data,
        is_validated: Number(response.data.validated_by_user ?? response.data.is_validated ?? suppliedCalculation.is_validated ?? 0) === 1,
      });
    } catch (error) {
      if (!isCurrentRequest()) return;
      setDetailsError(true);
      console.error('Erreur lors du chargement des détails de validation:', error);
      message.error('Erreur lors du chargement des détails');
    } finally {
      if (isCurrentRequest()) setIsLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!calculation?.id) return;
    
    try {
      await onValidate(calculation.id);
      await loadValidationDetails();
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
    }
  };

  const handleUnvalidate = async () => {
    if (!calculation?.id) return;
    
    try {
      await onUnvalidate(calculation.id);
      await loadValidationDetails();
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
    }
  };

  const getStatusIcon = (isValid: boolean, hasErrors: boolean, hasWarnings: boolean) => {
    if (isValid && !hasErrors) {
      return <CheckCircleOutlined style={{ color: token.colorSuccess }} />;
    }
    if (hasErrors) {
      return <CloseCircleOutlined style={{ color: token.colorError }} />;
    }
    if (hasWarnings) {
      return <ExclamationCircleOutlined style={{ color: token.colorWarning }} />;
    }
    return <ExclamationCircleOutlined style={{ color: token.colorTextSecondary }} />;
  };

  const renderErrorList = (errors: ValidationError[]) => (
    <List
      size="small"
      dataSource={errors}
      renderItem={(error, index) => (
        <List.Item key={index}>
          <Alert
            message={error.message}
            description={
              <div>
                <Text type="secondary">Champ: {error.field}</Text>
                <br />
                <Text type="secondary">
                  Valeur actuelle: {error.current_value}
                  {error.expected_value && ` | Attendu: ${error.expected_value}`}
                </Text>
              </div>
            }
            type="error"
            showIcon
            style={{ width: '100%' }}
          />
        </List.Item>
      )}
    />
  );

  const renderWarningList = (warnings: ValidationWarning[]) => (
    <List
      size="small"
      dataSource={warnings}
      renderItem={(warning, index) => (
        <List.Item key={index}>
          <Alert
            message={warning.message}
            description={
              <div>
                <Text type="secondary">Champ: {warning.field}</Text>
                <br />
                <Text type="secondary">
                  Valeur actuelle: {warning.current_value}
                  {warning.recommended_value && ` | Recommandé: ${warning.recommended_value}`}
                </Text>
              </div>
            }
            type="warning"
            showIcon
            style={{ width: '100%' }}
          />
        </List.Item>
      )}
    />
  );

  if (!calculation) return null;

  const hasErrors = (validationResult?.errors?.length || 0) > 0;
  const hasWarnings = (validationResult?.warnings?.length || 0) > 0;
  const isValid = validationResult?.is_valid || false;

  return (
    <Modal
      title={
        <Space>
          {getStatusIcon(isValid, hasErrors, hasWarnings)}
          <span>Validation - {calculation.employee_name}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={
        <Space>
          <Button onClick={onCancel}>Fermer</Button>
          {!calculation.is_validated ? (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleValidate}
              loading={loading}
              disabled={loading || isLoading || !validationResult || !validationResult.is_valid || hasErrors
                || validationResult.can_be_validated === false}
            >
              Valider
            </Button>
          ) : (
            <Button
              type="default"
              icon={<UndoOutlined />}
              onClick={handleUnvalidate}
              loading={loading}
            >
              Annuler la validation
            </Button>
          )}
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        {detailsError && (
          <Alert
            message="Vérification indisponible"
            description="Impossible de charger le contrôle de ce calcul. Fermez la fenêtre puis réessayez."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {/* Informations employé */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Descriptions title="Informations Employé" bordered size="small">
              <Descriptions.Item label="Nom" span={2}>
                <Space>
                  <UserOutlined />
                  {calculation.employee_name}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Code">
                {calculation.employee_code}
              </Descriptions.Item>
              <Descriptions.Item label="Département" span={2}>
                {calculation.department_name}
              </Descriptions.Item>
              <Descriptions.Item label="Période">
                <Space>
                  <CalendarOutlined />
                  {calculation.period_name}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        <Divider />

        {/* Résumé financier */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Salaire Brut"
              value={calculation.gross_salary || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined style={{ color: token.colorPrimary }} />}
              valueStyle={{ color: token.colorPrimary }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Déductions"
              value={Number(calculation.total_deductions ?? 0)}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined style={{ color: token.colorWarning }} />}
              valueStyle={{ color: token.colorWarning }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Salaire Net"
              value={calculation.net_salary || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined style={{ color: token.colorSuccess }} />}
              valueStyle={{ color: token.colorSuccess }}
            />
          </Col>
        </Row>

        <Divider />

        {/* Statut de validation */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              {calculation.is_validated ? (
                <Tag color="success" style={{ fontSize: '16px', padding: '8px 16px' }}>
                  <CheckCircleOutlined /> Calcul Validé
                </Tag>
              ) : (
                <Tag color="default" style={{ fontSize: '16px', padding: '8px 16px' }}>
                  <ExclamationCircleOutlined /> En attente de validation
                </Tag>
              )}
            </div>
          </Col>
        </Row>

        {/* Détails de validation */}
        {validationResult && (
          <Collapse defaultActiveKey={hasErrors || hasWarnings ? ['1'] : []}>
            <Panel
              header={
                <Space>
                  <span>Résultats de validation</span>
                  {hasErrors && <Tag color="error">{validationResult.errors.length} erreur(s)</Tag>}
                  {hasWarnings && <Tag color="warning">{validationResult.warnings.length} avertissement(s)</Tag>}
                  {!hasErrors && !hasWarnings && <Tag color="success">Aucun problème détecté</Tag>}
                </Space>
              }
              key="1"
            >
              {hasErrors && (
                <div style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ color: token.colorError }}>
                    <CloseCircleOutlined /> Erreurs détectées
                  </Title>
                  {renderErrorList(validationResult.errors)}
                </div>
              )}

              {hasWarnings && (
                <div style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ color: token.colorWarning }}>
                    <ExclamationCircleOutlined /> Avertissements
                  </Title>
                  {renderWarningList(validationResult.warnings)}
                </div>
              )}

              {!hasErrors && !hasWarnings && (
                <Alert
                  message="Validation réussie"
                  description="Aucun problème détecté dans ce calcul de paie."
                  type="success"
                  showIcon
                />
              )}
            </Panel>

            <Panel header="Détails du calcul" key="2">
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Salaire de base">
                  {formatCurrency(calculation.basic_salary || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Prime d'ancienneté">
                  {formatCurrency(calculation.seniority_bonus || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Indemnité transport">
                  {formatCurrency(calculation.transport_allowance || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Indemnité logement">
                  {formatCurrency(calculation.housing_allowance || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="CNSS Employé">
                  {formatCurrency(calculation.cnss_employee || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="IUTS">
                  {formatCurrency(calculation.iuts_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Avances">
                  {formatCurrency(calculation.advances || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Prêts">
                  {formatCurrency(calculation.loans || 0)}
                </Descriptions.Item>
              </Descriptions>
            </Panel>
          </Collapse>
        )}

        {/* Information sur les actions */}
        {hasErrors && (
          <Alert
            message="Validation bloquée"
            description="Ce calcul contient des erreurs qui doivent être corrigées avant validation."
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Spin>
    </Modal>
  );
};

export default ModalValidationIndividuelle;
